import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useCallback, useMemo, useState } from 'react';

import { type VendorActivityDataType, useVendorActivity } from '@/api/vendor';
import type { DataTableColumnDef } from '@/components/DataTable';
import {
  FitScoreLegend,
  FitScoreMetricCell,
  FitScoreTable,
  FitScoreTitleCell,
} from '@/components/FitScoreTable';
import {
  InstantSearchSortOrder,
  type SolQueryParamsNew,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { SupportLink } from '@/components/typography';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';

import VendorFitScoringTableActions from './VendorFitScoringTableActions';

const TITLE_WIDTH = 160;

const VendorFitScoringTable = () => {
  const { pageFilters } = usePageFilterContext();
  const { queryState } = useInstantSearchState();
  const { startDate, endDate, audience } = pageFilters;
  const solQueryParams = getSolQueryParamsNewFromQueryState(queryState);

  const fitScoreQueryParams = useMemo<SolQueryParamsNew | undefined>(() => {
    if (!solQueryParams) {
      return undefined;
    }

    return {
      ...solQueryParams,
      sort: { field: 'fitScore', direction: InstantSearchSortOrder.DESC },
      filter: [
        ...solQueryParams.filter,
        {
          field: 'visits',
          operator: 'gt',
          operand: '0',
        },
        {
          field: 'spend',
          operator: 'gt',
          operand: '0',
        },
      ],
    };
  }, [solQueryParams]);
  const {
    vendorActivity: vendorFitScores,
    isLoading,
    error,
  } = useVendorActivity(fitScoreQueryParams, startDate, endDate, audience?.id);
  const [removedVendors, setRemovedVendors] = useState<VendorActivityDataType[]>([]);

  const handleRemoveVendor = useCallback(
    (vendorToRemove: VendorActivityDataType) => {
      setRemovedVendors([...removedVendors, vendorToRemove]);
    },
    [removedVendors, setRemovedVendors],
  );

  const handleAddVendor = useCallback(
    (vendorToAdd: VendorActivityDataType) => {
      setRemovedVendors(removedVendors.filter((vendor) => vendor !== vendorToAdd));
    },
    [removedVendors, setRemovedVendors],
  );

  const visibleVendorFitScores = useMemo(() => {
    return vendorFitScores?.filter((vendor) => !removedVendors.includes(vendor));
  }, [removedVendors, vendorFitScores]);

  const columns = useMemo<DataTableColumnDef<VendorActivityDataType>[]>(
    () => [
      {
        id: 'vendor',
        header: t`Vendor`,
        size: TITLE_WIDTH,
        minSize: TITLE_WIDTH,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreTitleCell
              title={field.vendor.name}
              subtitle={field.vendor.channel?.name}
              titleLink={ROUTES.vendorById(field.vendor.id)}
              onRemove={() => handleRemoveVendor(field)}
            />
          );
        },
      },
      {
        id: 'allVisits',
        accessorKey: 'allVisits',
        header: t`Visits`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.allVisitsBest}
              isWorst={field.allVisitsWorst}
              label={<Trans>Visits</Trans>}
              value={numberFormat(field.allVisits)}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'engagedCompanies',
        accessorKey: 'engagedCompanies',
        header: t`Engaged Companies`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.engagedCompaniesBest}
              isWorst={field.engagedCompaniesWorst}
              label={<Trans>Engaged Companies</Trans>}
              value={numberFormat(field.percentOfEngagedCompanies, {
                isPercent: true,
                precision: 1,
              })}
              caption={numberFormat(field.engagedCompanies)}
              captionLink={ROUTES.webTrafficActivityWithParams({
                'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
                'w.vendorIds': field.vendor.id,
                'w.metrics.visits': '[1,]',
              })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'spendPerVisit',
        accessorKey: 'spendPerTargetVisit',
        header: t`$/Visit`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.spendPerVisitBest}
              isWorst={field.spendPerVisitWorst}
              label={<Trans>$/Visit</Trans>}
              value={numberFormat(field.spendPerTargetVisit, { isCurrency: true, precision: 2 })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'pipelineInfluencedPerSpend',
        accessorKey: 'pipelineInfluencedPerSpend',
        header: t`$/Pipeline Influenced`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.pipelineInfluencedBest}
              isWorst={field.pipelineInfluencedWorst}
              label={<Trans>$/Pipeline Influenced</Trans>}
              value={numberFormat(field.pipelineInfluencedPerSpend, {
                isMultiplier: true,
                precision: 1,
              })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'percentOpportunitiesInfluenced',
        accessorKey: 'percentOpportunitiesInfluenced',
        header: t`% Opps Influenced`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.opportunityInfluencedBest}
              isWorst={field.opportunityInfluencedWorst}
              label={<Trans>% Opps Influenced</Trans>}
              value={numberFormat(field.percentOpportunitiesInfluenced, {
                isPercent: true,
                precision: 0,
              })}
              caption={numberFormat(field.opportunitiesInfluenced)}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'fitScore',
        accessorKey: 'fitScore',
        header: t`Fit Score`,
        minSize: 173,
        flex: 1,
        enableSorting: true,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.fitScoreBest}
              isWorst={field.fitScoreWorst}
              label={<Trans>Fit Score</Trans>}
              value={numberFormat(field.fitScore, { precision: 1 })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
    ],
    [audience?.id, audience?.isTAM, handleRemoveVendor, error],
  );

  const countBaseAccounts = vendorFitScores?.length && vendorFitScores[0].baseAccounts;

  return (
    <Panel
      title={
        <Trans>
          Fit Score - {audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`)}
          {countBaseAccounts != null ? ' ' + t`(${numberFormat(countBaseAccounts)} Companies)` : ''}
        </Trans>
      }
      infoPopover={{
        title: t`Fit Score`,
        body: (
          <Trans>
            The Vendor Fit Score is an analysis of 5 major metrics used to calculate vendor
            performance from 1-100.{' '}
            <SupportLink variant="body2" color="static-green" path="FIT_SCORE">
              Learn more
            </SupportLink>
          </Trans>
        ),
      }}
      noPadding
    >
      <FitScoreLegend />
      <FitScoreTable<VendorActivityDataType>
        columns={columns}
        dataSource={visibleVendorFitScores}
        emptyMessage={t`Please add vendor spend data to see fit scores.`}
        loading={isLoading}
        error={error}
        rowKey={(record) => `${record.vendor.channel?.id}-${record.vendor.id}`}
        initialColumnPinning={{ left: ['vendor'], right: ['fitScore'] }}
        defaultSorting={[{ id: 'fitScore', desc: true }]}
        actionContent={
          removedVendors.length > 0 && (
            <VendorFitScoringTableActions
              titleWidth={TITLE_WIDTH}
              removedVendors={removedVendors}
              onAddVendor={handleAddVendor}
            />
          )
        }
      />
    </Panel>
  );
};

export default VendorFitScoringTable;
